import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import axios from '@/utils/api';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import { CurrencyDollar, Article, Briefcase, CursorClick, TrendUp } from '@phosphor-icons/react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';

const DONUT_COLORS = ['#DC120F', '#0ea5e9', '#22c55e', '#f59e0b'];
const CHART_TICK = { fill: '#94a3b8', fontSize: 12 };
const TT_STYLE = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', color: '#0f172a' };

const REVENUE_TREND = [
  { month: 'Nov', earnings: 8200 },
  { month: 'Dec', earnings: 11400 },
  { month: 'Jan', earnings: 13600 },
  { month: 'Feb', earnings: 14800 },
  { month: 'Mar', earnings: 17200 },
  { month: 'Apr', earnings: 18800 },
];

const CAMPAIGN_SHARE = [
  { name: 'Amazon TMOE', value: 48 },
  { name: 'Skyscanner', value: 31 },
  { name: 'AEO Campaign', value: 21 },
];

const CLICKS_IMPRESSIONS = [
  { month: 'Nov', clicks: 5200, impressions: 42000 },
  { month: 'Dec', clicks: 6100, impressions: 48000 },
  { month: 'Jan', clicks: 6800, impressions: 52000 },
  { month: 'Feb', clicks: 7200, impressions: 58000 },
  { month: 'Mar', clicks: 8400, impressions: 64000 },
  { month: 'Apr', clicks: 8800, impressions: 72000 },
];

const KPI = [
  { title: 'Total Earnings', value: '₹84,000', trend: '+14.2%', icon: CurrencyDollar, color: '#DC120F' },
  { title: 'Articles Published', value: '12', trend: '+3', icon: Article, color: '#0ea5e9' },
  { title: 'Active Campaigns', value: '3', trend: null, icon: Briefcase, color: '#22c55e' },
  { title: 'Clicks Generated', value: '42,500', trend: '+8.6%', icon: CursorClick, color: '#f59e0b' },
];

export default function PublisherDashboard() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/publisher/campaigns`);
        setCampaigns(res.data);
      } catch {
        toast.error('Failed to load campaigns');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <DashboardLayout role="publisher">
        <div className="p-8 bg-muted min-h-screen">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="publisher">
      <div className="p-8 bg-muted min-h-screen" data-testid="publisher-dashboard">
        <h1 className="font-heading text-4xl font-bold mb-8 text-foreground">Dashboard</h1>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {KPI.map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.title} className="bg-white rounded-xl border p-5 hover:shadow-lg transition-shadow" data-testid={`kpi-${k.title.toLowerCase().replace(/ /g,'-')}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="rounded-lg p-2" style={{ background: `${k.color}15` }}>
                    <Icon size={20} style={{ color: k.color }} />
                  </div>
                  {k.trend && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                      <TrendUp size={12} /> {k.trend}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{k.title}</p>
                <p className="mt-1 text-2xl font-bold text-foreground">{k.value}</p>
              </div>
            );
          })}
        </div>

        {/* Charts Row */}
        <div className="grid gap-6 xl:grid-cols-3 mb-8">
          {/* Earnings Trend */}
          <div className="bg-white rounded-xl border p-5 xl:col-span-2">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-foreground">Earnings Trend</h2>
              <span className="text-xs text-muted-foreground">Monthly Earnings</span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={REVENUE_TREND}>
                  <defs>
                    <linearGradient id="pubFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#DC120F" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#DC120F" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="month" tick={CHART_TICK} axisLine={false} tickLine={false} />
                  <YAxis tick={CHART_TICK} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                  <Tooltip contentStyle={TT_STYLE} formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, 'Earnings']} />
                  <Area type="monotone" dataKey="earnings" stroke="#DC120F" strokeWidth={3} fill="url(#pubFill)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Campaign Share */}
          <div className="bg-white rounded-xl border p-5">
            <h2 className="text-lg font-bold text-foreground mb-5">Campaign Share</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={CAMPAIGN_SHARE} dataKey="value" nameKey="name" cx="50%" cy="45%" innerRadius={50} outerRadius={78} paddingAngle={3}>
                    {CAMPAIGN_SHARE.map((entry, i) => (
                      <Cell key={entry.name} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TT_STYLE} formatter={(v) => [`${v}%`, 'Share']} />
                  <Legend verticalAlign="bottom" height={36} formatter={(v) => <span style={{ color: '#0f172a', fontSize: 11 }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Clicks vs Impressions */}
        <div className="bg-white rounded-xl border p-5 mb-8">
          <h2 className="text-lg font-bold text-foreground mb-5">Clicks vs Impressions</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={CLICKS_IMPRESSIONS}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={CHART_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={CHART_TICK} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TT_STYLE} />
                <Bar dataKey="clicks" name="Clicks" fill="#DC120F" radius={[6, 6, 0, 0]} />
                <Bar dataKey="impressions" name="Impressions" fill="#d4d4d4" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Campaigns Table */}
        <div className="bg-white rounded-xl border" data-testid="recent-campaigns-section">
          <div className="border-b p-6">
            <h2 className="text-lg font-bold text-foreground">Recent Campaigns</h2>
          </div>
          <div className="p-6">
            {campaigns.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No campaigns yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="pb-3">Campaign</th>
                      <th className="pb-3">Category</th>
                      <th className="pb-3">Status</th>
                      <th className="pb-3 text-right">Budget</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.slice(0, 5).map((c) => (
                      <tr key={c.id} className="border-b">
                        <td className="py-3 font-medium">{c.name}</td>
                        <td className="py-3 text-muted-foreground">{c.category}</td>
                        <td className="py-3">
                          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${c.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground'}`}>
                            {c.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 text-right font-mono">₹{(c.content_budget + c.distribution_budget).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
